// react
import {
  combineReducers,
  configureStore,
  type UnknownAction,
} from '@reduxjs/toolkit';
import {
  localStorageMiddleware,
  mixpanelTrackingMiddleware,
  preloadState,
} from './middleware';
import {
  useSelector as useReduxSelector,
  useDispatch as useReduxDispatch,
} from 'react-redux';

// store
import api from './api';
import iamReducer from './slices/iamSlice';
import careCircleReducer from './slices/careCircleSlice';
import chatReducer from './slices/chatSlice';
import navigationReducer from './slices/navigationSlice';
import uiReducer from './slices/uiSlice';
import cmsReducer from './slices/cmsSlice';
import registrationReducer from './slices/registrationSlice';
import clientReferralReducer from './slices/clientReferralSlice';
import onboardingV3Reducer from './slices/onboardingV3Slice';
import benefitsCalculatorReducer from './slices/benefitsCalculatorSlice';

// reducers
const rootReducer = combineReducers({
  [api.reducerPath]: api.reducer,
  ui: uiReducer,
  iam: iamReducer,
  chat: chatReducer,
  careCircle: careCircleReducer,
  navigation: navigationReducer,
  cms: cmsReducer,
  registration: registrationReducer,
  clientReferral: clientReferralReducer,
  onboardingV3: onboardingV3Reducer,
  benefitsCalculator: benefitsCalculatorReducer,
});

// Dev/staging: allow full state replacement from the state inspector
export const DEV_REPLACE_STATE_TYPE = '__DEV_REPLACE_STATE__';
const isNonProd = import.meta.env.REACT_APP_BUILD_ENVIRONMENT !== 'production';

const reducer = isNonProd
  ? (state: RootState | undefined, action: UnknownAction) => {
      if (
        action.type === DEV_REPLACE_STATE_TYPE &&
        'payload' in action &&
        action.payload
      ) {
        return action.payload as RootState;
      }
      return rootReducer(state, action);
    }
  : rootReducer;

// create store
const store = configureStore({
  reducer,
  preloadedState: preloadState(),
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware()
      .concat(api.middleware)
      .concat(mixpanelTrackingMiddleware)
      .concat(localStorageMiddleware),
  devTools: import.meta.env.NODE_ENV !== 'production',
});

// types
export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;

// hooks
export const useSelector = useReduxSelector.withTypes<RootState>();
export const useDispatch = useReduxDispatch.withTypes<AppDispatch>();

export default store;
export const { dispatch } = store;
